/** XeAgents.ai**/
import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_PRICING_REGION,
  PRICING_REGION_CURRENCY,
  detectPricingRegion,
  regionFromCountry,
  type PricingRegion,
} from "@shared/geo-pricing";

/**
 * Exported because signup reads it too: a visitor who picked a currency on the
 * pricing grid has told us where they are, and the register form's country
 * field should start there rather than on its own guess.
 */
export const PRICING_REGION_STORAGE_KEY = "voicepro.pricing-region";

function readStoredRegion(): PricingRegion | null {
  try {
    const stored = window.localStorage.getItem(PRICING_REGION_STORAGE_KEY);
    return stored === "IN" || stored === "US" ? stored : null;
  } catch {
    // Private mode and blocked site data both throw here.
    return null;
  }
}

function storeRegion(region: PricingRegion): void {
  try {
    window.localStorage.setItem(PRICING_REGION_STORAGE_KEY, region);
  } catch {
    // Not being able to remember the choice is not worth failing the render.
  }
}

/**
 * The region the browser can work out on its own, with no network round trip.
 * Runs synchronously so the first paint already carries the right currency -
 * a price that flips from $ to ₹ a moment after it appears reads as a bug.
 */
function detectLocalRegion(): PricingRegion {
  if (typeof window === "undefined") return DEFAULT_PRICING_REGION;

  let timeZone: string | null = null;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    timeZone = null;
  }

  const locales = navigator.languages?.length
    ? Array.from(navigator.languages)
    : navigator.language
      ? [navigator.language]
      : [];

  return detectPricingRegion({ timeZone, locales });
}

export interface UsePricingRegionResult {
  /** 'IN' quotes INR, 'US' quotes USD - the only two price lists that exist. */
  region: PricingRegion;
  currency: "INR" | "USD";
  /** True once the visitor has picked a currency themselves. */
  isManual: boolean;
  chooseRegion: (region: PricingRegion) => void;
}

/**
 * Decides which price list a landing-page visitor sees.
 *
 * Strongest signal wins: an explicit choice they made here before, then the
 * country an edge/CDN resolved from their IP, then their browser's time zone
 * and languages. The browser signals resolve synchronously and the server one
 * only ever corrects them, so nothing waits on a request.
 */
export function usePricingRegion(): UsePricingRegionResult {
  const [override, setOverride] = useState<PricingRegion | null>(() =>
    typeof window === "undefined" ? null : readStoredRegion(),
  );
  const [localRegion] = useState<PricingRegion>(detectLocalRegion);
  const [headerRegion, setHeaderRegion] = useState<PricingRegion | null>(null);

  // Asking the server is pointless once they have chosen for themselves.
  const skipLookup = override !== null;

  useEffect(() => {
    if (skipLookup) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch("/api/geo/country", {
          signal: AbortSignal.timeout(4000),
        });
        if (!res.ok) return;
        const region = regionFromCountry((await res.json())?.region);
        if (!cancelled && region) setHeaderRegion(region);
      } catch {
        // No edge header, no network: the browser signals already answered.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [skipLookup]);

  const chooseRegion = useCallback((next: PricingRegion) => {
    setOverride(next);
    storeRegion(next);
  }, []);

  const region = override ?? headerRegion ?? localRegion;

  return {
    region,
    currency: PRICING_REGION_CURRENCY[region],
    isManual: override !== null,
    chooseRegion,
  };
}

export default usePricingRegion;
